import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from 'src/environments/environment';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
  }
}

/**
 * Google-Ads-Conversion-Tracking fuer die separate Registrierungs-App.
 *
 * Der Service bleibt ohne explizite Konfiguration vollstaendig inaktiv. Eine
 * Registrierung wird beim Absenden gemeldet, sobald der Server die Anfrage mit 200
 * angenommen hat. Seit BE-12 P5 (e6d3b9df) ist `CompanyId` immer 0 (gleiche Antwort
 * fuer neue und bekannte Adressen); transaction_id und lokale Deduplizierungs-ID ist
 * deshalb die je Aufruf zufaellige `RegistrationId` der Antwort.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAdsConversionService {
  private readonly sharedConsentCookie = 'fp_marketing_consent';
  private readonly isBrowser: boolean;
  private initialized = false;
  private marketingGranted = false;
  private scriptRequested = false;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  initialize(marketingGranted: boolean): void {
    if (!this.isBrowser) return;

    this.ensureGtagStub();
    if (!this.initialized) {
      window.gtag?.('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500,
      });
      window.gtag?.('set', 'ads_data_redaction', true);
      this.initialized = true;
    }

    this.marketingGranted = marketingGranted;
    const state = marketingGranted ? 'granted' : 'denied';
    window.gtag?.('consent', 'update', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      // Auf der Registrierungs-App gibt es derzeit keine separate Analyse-Einwilligung.
      analytics_storage: 'denied',
    });

    if (marketingGranted) this.loadTagIfConfigured();
  }

  /** Liest nur den nicht-identifizierenden, domainweiten Consent-Status. */
  hasTransferredMarketingConsent(): boolean {
    if (!this.isBrowser) return false;
    return document.cookie
      .split(';')
      .map((part) => part.trim())
      .some((part) => part === `${this.sharedConsentCookie}=granted`);
  }

  /**
   * @returns true, wenn das Conversion-Event in die gtag-Queue geschrieben wurde.
   */
  trackRegistrationCompleted(response: unknown): boolean {
    const config = environment.googleAds;
    if (
      !this.isBrowser ||
      !this.marketingGranted ||
      !config.enabled ||
      !this.isValidTagId(config.tagId) ||
      !this.isValidSendTo(config.registration.sendTo)
    ) {
      return false;
    }

    const transactionId = this.extractRegistrationId(response);
    if (transactionId === null) {
      console.warn('[Google Ads] Keine RegistrationId in der Registrierungsantwort; Conversion nicht gesendet.');
      return false;
    }

    const dedupeKey = `fp_google_ads_registration_sent:${transactionId}`;
    if (sessionStorage.getItem(dedupeKey) === '1') return false;

    this.loadTagIfConfigured();
    window.gtag?.('event', 'conversion', {
      send_to: config.registration.sendTo,
      value: config.registration.value,
      currency: config.registration.currency,
      transaction_id: transactionId,
    });
    sessionStorage.setItem(dedupeKey, '1');
    return true;
  }

  private extractRegistrationId(response: unknown): string | null {
    if (!response || typeof response !== 'object') return null;
    const value = response as Record<string, unknown>;
    const data = (value['Data'] ?? value['data']) as Record<string, unknown> | undefined;
    const candidate =
      data?.['RegistrationId'] ??
      data?.['registrationId'] ??
      value['RegistrationId'] ??
      value['registrationId'];
    if (typeof candidate !== 'string') return null;
    const id = candidate.trim();
    // Guid.Empty wäre keine Registrierung, sondern ein Default — nicht melden.
    return id && !/^[0-]+$/.test(id) ? id : null;
  }

  private loadTagIfConfigured(): void {
    const config = environment.googleAds;
    if (this.scriptRequested || !config.enabled || !this.isValidTagId(config.tagId)) return;

    this.scriptRequested = true;
    const script = document.createElement('script');
    script.id = 'fixplaner-google-ads-tag';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.tagId)}`;
    document.head.appendChild(script);

    window.gtag?.('js', new Date());
    window.gtag?.('config', config.tagId, { send_page_view: false });
  }

  private isValidTagId(tagId: string): boolean {
    return /^AW-\d+$/.test(tagId);
  }

  private isValidSendTo(sendTo: string): boolean {
    return /^AW-\d+\/[A-Za-z0-9_-]+$/.test(sendTo);
  }

  private ensureGtagStub(): void {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer.push(args));
  }
}
